import React from 'react';
import { ChevronDown, ChevronRight, PackageMinus } from 'lucide-react';
import type { InventoryItem, RoastedBulkGroup } from '../../types/inventory';
import { formatCurrency, formatDecimal } from '../../utils/format';
import { RoastedBulkGroupItemsTable } from './RoastedBulkGroupItemsTable';
import { useLanguage } from '../../features/language/useLanguage';

interface RoastedBulkGroupsTableProps {
  groups: RoastedBulkGroup[];
  statusFilter: 'all' | 'active' | 'inactive';
  expandedKeys: Set<string>;
  toggleGroup: (key: string) => void;
  currencySymbol: string;
  items: InventoryItem[];
  onEditItem?: (itemId: string) => void;
  onOpenHistory: (itemId: string) => void;
  onOpenRoastProfile: (context: { itemId: string; itemName: string; workOrderId?: string }) => void;
  onAdjustStock?: (group: RoastedBulkGroup) => void;
  canWrite?: boolean;
}

export const RoastedBulkGroupsTable: React.FC<RoastedBulkGroupsTableProps> = ({
  groups,
  statusFilter,
  expandedKeys,
  toggleGroup,
  currencySymbol,
  items,
  onEditItem,
  onOpenHistory,
  onOpenRoastProfile,
  onAdjustStock,
  canWrite,
}) => {
  const { t } = useLanguage();
  return (
    <div className="hidden md:block overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('reports.shift.item')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('workOrders.table.roastType')}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.totalStockKg')}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.averagePrice')}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.actions')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200 uppercase font-semibold">
          {groups.map(group => {
            const key = `${group.itemId}:${group.roastType}`;
            const isExpanded = expandedKeys.has(key);
            
            return (
              <React.Fragment key={key}>
                <tr
                  data-test-id={`inventory-roasted-group-header-${group.itemId}-${group.roastType}`}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    if (onEditItem && canWrite) {
                      onEditItem(group.itemId);
                      return;
                    }
                    toggleGroup(key);
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-test-id={`inventory-roasted-group-toggle-${group.itemId}-${group.roastType}`}
                        onClick={event => {
                          event.stopPropagation();
                          toggleGroup(key);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                      >
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                      <div>
                        <div>{group.itemName}</div>
                        <div className="text-xs text-gray-500">{group.itemCode}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {group.roastType || t('inventory.roastedBulk.unknownRoastType')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    {formatDecimal(group.totalStock, 3)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    {formatCurrency(typeof group.averagePrice === 'number' ? group.averagePrice : 0, currencySymbol)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right normal-case">
                    {canWrite && onAdjustStock && (
                      <button
                        type="button"
                        data-test-id={`inventory-roasted-group-adjust-${group.itemId}-${group.roastType}`}
                        onClick={event => {
                          // The row's own onClick opens item edit; this must not.
                          event.stopPropagation();
                          onAdjustStock(group);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-md bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-950 transition-colors hover:bg-primary-100"
                      >
                        <PackageMinus className="h-4 w-4" />
                        {t('inventory.roastedBulk.adjust.button')}
                      </button>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bg-gray-50">
                    <td colSpan={6} className="px-6 py-4">
                      <RoastedBulkGroupItemsTable
                        group={group}
                        statusFilter={statusFilter}
                        currencySymbol={currencySymbol}
                        items={items}
                        onEditItem={onEditItem}
                        onOpenHistory={onOpenHistory}
                        onOpenRoastProfile={onOpenRoastProfile}
                        canWrite={canWrite}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
